import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { debounce } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { queryClient } from '../../../services/MaggieQueryClient';
import { fetchCompanyMetrics } from '../../../services/queries/MaggieMetricsQueries';
import {
  fetchAllInvestmentRounds,
  fetchTransactionsByCompany2,
} from '../../../services/queries/MaggieTransactionQueries';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { setCompanyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { FormatterService } from '../../../util/formatter-service';
import { InvestmentRoundFormatter } from '../../../util/formatters/InvestmentRoundFormatter';
import { StageFormatter } from '../../../util/formatters/StageFormatter';
import { useSelectedCurrency } from '../../../hooks/useSelectedCurrency';
import { forceFundMetricsUpdateJ, MetricsQueryKey } from '../../PortfolioOverview/state/MetricsStateJ';
import { RoundTrackerQueryKey } from '../../PortfolioOverview/state/RoundTrackerMetricsStateJ';
import {
  allInvestmentRoundsStateJ,
  financeTransactionsByCompanyStateJ,
  forceAllTransactionsByDateUpdateJ,
} from '../state/FinanceStateJ';
import { useFetchAndSavePortCos } from './usePortCos';

export function useInvalidateFinanceData() {
  const fetchAndSavePortCos = useFetchAndSavePortCos();
  const roundsById = useAtomValue(roundsByIdMapAtom);
  const { selectedCurrency } = useSelectedCurrency();

  const invalidateInvestments = useAtomCallback(
    useCallback(
      async (_get, set) => {
        try {
          const data = await fetchAllInvestmentRounds();
          set(allInvestmentRoundsStateJ, data);
          const investmentRoundFormatter = new InvestmentRoundFormatter().init(
            new Map(data.map((round) => [round.id, round]))
          );
          FormatterService.get().setFormatterForId('investmentRound', investmentRoundFormatter.format);
          FormatterService.get().setFormatterForId(
            'stage',
            new StageFormatter().init({
              investmentMap: new Map(data.map((investmentRound) => [investmentRound.id, investmentRound])),
              stageMap: roundsById,
            }).format
          );
        } catch (err) {
          console.error(err);
        }
      },
      [roundsById]
    )
  );

  const invalidateTransactions = useAtomCallback(
    useCallback(async (_get, set, companyId: number) => {
      try {
        const data = await fetchTransactionsByCompany2(companyId);
        set(financeTransactionsByCompanyStateJ(companyId), data);
        set(forceAllTransactionsByDateUpdateJ, (curr) => curr + 1);
      } catch (err) {
        console.error(err);
      }
    }, [])
  );

  const invalidatePortCos = useCallback(async () => {
    try {
      await fetchAndSavePortCos();
    } catch (err) {
      console.error(err);
    }
  }, [fetchAndSavePortCos]);

  const invalidateMetrics = useAtomCallback(
    useCallback(
      async (_get, set, companyId: number) => {
        set(forceFundMetricsUpdateJ, (curr) => curr + 1);
        queryClient.invalidateQueries({ queryKey: [RoundTrackerQueryKey] });
        queryClient.invalidateQueries({ queryKey: [MetricsQueryKey] });
        const companyMetrics = await fetchCompanyMetrics(companyId, undefined, selectedCurrency);
        set(setCompanyMetricsAtomJ, {
          params: { companyId, currency: selectedCurrency },
          value: companyMetrics,
        });
      },
      [selectedCurrency]
    )
  );
  const debouncedInvalidateMetrics = useMemo(
    () => debounce((companyId: number) => invalidateMetrics(companyId), 0),
    [invalidateMetrics]
  );

  const invalidateAll = useCallback(
    async (companyId: number) => {
      await Promise.all([
        invalidateInvestments(),
        invalidateTransactions(companyId),
        debouncedInvalidateMetrics(companyId),
        invalidatePortCos(),
      ]);
    },
    [invalidateInvestments, debouncedInvalidateMetrics, invalidatePortCos, invalidateTransactions]
  );

  return {
    invalidateInvestments,
    invalidateTransactions,
    invalidatePortCos,
    invalidateMetrics,
    invalidateAll,
  };
}
